import { ReactNode, useState, useEffect } from "react";
import { ArrowLeft, Share2, Sparkles, Zap, Star, Clock, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import DesktopFloatingCTA from "@/components/DesktopFloatingCTA";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

interface BlogLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  date: string;
  readTime: string;
  author?: string;
  category: string;
  categoryColor?: string;
}

const BlogLayout = ({
  children,
  title,
  subtitle,
  date,
  readTime,
  author = "Fuel72 Team",
  category,
  categoryColor = "from-neon-purple to-neon-pink",
}: BlogLayoutProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    setIsLoaded(true);
    
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: title,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t("blog.linkCopied"));
      }
    } catch (error) {
      console.log("Share failed:", error);
    }
  };

  return (
    <div className="min-h-screen gradient-mesh">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-background/50 z-[60]">
        <div 
          className="h-full bg-gradient-to-r from-neon-purple via-neon-pink to-neon-cyan transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      
      <Navbar />
      
      {/* Floating Decorative Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-40 left-[5%] text-4xl animate-float opacity-20">📖</div>
        <div className="absolute top-60 right-[8%] text-3xl animate-float opacity-15" style={{ animationDelay: "1s" }}>✨</div>
        <div className="absolute bottom-40 left-[10%] text-3xl animate-float opacity-15" style={{ animationDelay: "2s" }}>💡</div>
      </div>
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className={`pt-32 pb-16 px-4 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="container mx-auto max-w-4xl">
            {/* Back Button */}
            <Button
              variant="ghost"
              onClick={() => navigate("/blog")}
              className="mb-8 group hover:bg-white/5"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              {t("blog.backToBlog")}
            </Button>
            
            {/* Article Header Card */}
            <div className="relative p-8 md:p-12 rounded-3xl glass-morphism border border-white/10 overflow-hidden">
              {/* Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/10 via-transparent to-neon-cyan/10 opacity-50" />
              
              {/* Content */}
              <div className="relative z-10">
                {/* Category Badge */}
                <div className="inline-flex items-center gap-2 mb-6">
                  <span className={`px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r ${categoryColor} text-white shadow-lg`}>
                    {category}
                  </span>
                  <Sparkles className="w-4 h-4 text-neon-purple animate-pulse" />
                </div>
                
                {/* Title */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 leading-tight">
                  <span className="bg-gradient-to-r from-white via-white to-white/80 bg-clip-text text-transparent">
                    {title}
                  </span>
                </h1>
                {subtitle && (
                  <p className="text-lg md:text-xl text-muted-foreground mb-4">{subtitle}</p>
                )}
                
                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-neon-purple to-neon-pink flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span>{author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-neon-cyan" />
                    <span>{readTime}</span>
                  </div>
                  <span className="text-foreground/50">{date}</span>
                  
                  {/* Share Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleShare}
                    className="ml-auto hover:bg-white/10 group"
                  >
                    <Share2 className="w-4 h-4 mr-2 group-hover:text-neon-purple transition-colors" />
                    {t("blog.share")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Article Content */}
        <section className={`pb-20 px-4 transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <article className="container mx-auto max-w-3xl">
            <div className="prose-premium">
              {children}
            </div>
            
            {/* Gradient Divider */}
            <div className="blog-section-divider" />
            
            {/* Bottom CTA Card */}
            <div className="mt-16 p-8 md:p-10 rounded-3xl glass-morphism border border-neon-purple/30 text-center relative overflow-hidden group hover:border-neon-purple/50 transition-all duration-500">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/20 via-transparent to-neon-cyan/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-neon-purple" />
                  <span className="text-sm font-medium text-neon-purple">{t("blog.readyToStart")}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  {t("blog.getVideosIn72h")}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  {t("blog.joinHundreds")}
                </p>
                <Button
                  onClick={() => navigate("/order")}
                  className="bg-gradient-to-r from-neon-purple to-neon-pink hover:opacity-90 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-neon-purple/30"
                >
                  <Star className="w-5 h-5 mr-2" />
                  {t("blog.orderPack")}
                </Button>
              </div>
            </div>
          </article>
        </section>
        
        {/* Related Articles Section */}
        <section className={`py-16 px-4 border-t border-white/5 transition-all duration-1000 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles className="w-5 h-5 text-neon-cyan" />
              <h2 className="text-2xl font-bold">{t("blog.relatedArticles")}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div 
                onClick={() => navigate("/blog/what-is-ai-ugc")}
                className="p-6 rounded-2xl glass-morphism border border-white/10 cursor-pointer hover:border-neon-purple/40 transition-all duration-300 group"
              >
                <span className="text-xs text-neon-purple font-medium">{t("blog.categoryGuide")}</span>
                <h3 className="text-lg font-semibold mt-2 group-hover:text-neon-cyan transition-colors">{t("blog.relatedWhatIsAiUgc")}</h3>
                <p className="text-sm text-muted-foreground mt-2">{t("blog.relatedWhatIsAiUgcDesc")}</p>
              </div>
              <div 
                onClick={() => navigate("/blog/ai-ugc-vs-traditional")}
                className="p-6 rounded-2xl glass-morphism border border-white/10 cursor-pointer hover:border-neon-purple/40 transition-all duration-300 group"
              >
                <span className="text-xs text-neon-pink font-medium">{t("blog.categoryComparison")}</span>
                <h3 className="text-lg font-semibold mt-2 group-hover:text-neon-cyan transition-colors">{t("blog.relatedAiVsTraditional")}</h3>
                <p className="text-sm text-muted-foreground mt-2">{t("blog.relatedAiVsTraditionalDesc")}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <StickyMobileCTA />
      <DesktopFloatingCTA />
    </div>
  );
};

export default BlogLayout;